import type { PopoverContentProps } from '@chakra-ui/react';
import { Button, ButtonGroup, Popover } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface FilterButtonMenuProps extends PopoverContentProps {
  children: React.ReactNode;
  // Optional: Prop to control if Apply/Cancel buttons are shown
  showFooterButtons?: boolean;
  // Optional: Callback when Apply button is clicked in the footer
  onApply?: () => void;
}

export const FilterButtonMenu: React.FC<FilterButtonMenuProps> = ({
  children,
  showFooterButtons = true, // Default to showing buttons
  onApply,
  ...popoverContentProps
}) => {
  const { t } = useTranslation();

  const handleApply = () => {
    onApply?.(); // Call the provided onApply callback
  };

  return (
    <Popover.Content
      rounded="14px"
      bg="rgba(255, 255, 255, 0.88)"
      backdropFilter="blur(16px)"
      // A hairline, not the popover's default drop shadow: the frosted panel
      // already lifts off the page on its own.
      boxShadow="none"
      borderWidth="1px"
      borderColor="cardBorder"
      {...popoverContentProps}
    >
      <Popover.CloseTrigger />
      <Popover.Body p={0}>{children}</Popover.Body>
      {showFooterButtons && (
        <Popover.Footer
          borderTopWidth="1px"
          borderColor="cardBorder"
          display="flex"
          justifyContent="flex-end"
          p={2}
        >
          <ButtonGroup size="xs" gap={2} fontFamily="heading">
            <Popover.CloseTrigger asChild>
              {/* Named here: the close trigger otherwise labels every button it wraps "close". */}
              <Button
                aria-label={t('common.cancel')}
                variant="outline"
                px={3}
                fontSize="12.5px"
                fontWeight="semibold"
                bg="white"
                borderColor="hairline.control"
                color="primary.900"
                _hover={{ bg: 'softCream' }}
              >
                {t('common.cancel')}
              </Button>
            </Popover.CloseTrigger>
            <Popover.CloseTrigger asChild>
              <Button
                aria-label={t('common.apply')}
                colorPalette="primary"
                px={3}
                fontSize="12.5px"
                fontWeight="semibold"
                onClick={handleApply}
              >
                {t('common.apply')}
              </Button>
            </Popover.CloseTrigger>
          </ButtonGroup>
        </Popover.Footer>
      )}
    </Popover.Content>
  );
};
